import React, { useState, useEffect } from 'react';
import useTranslation from 'Hooks/useTranslation';
import uploadFiles from 'Images/uploadfiles.svg';
import AlertMessage from 'Components/Alert';
import Modal from 'Components/Modal';
import Options from 'Components/Options';
import { TextField, Alert, MenuItem, Backdrop } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import useModal from '../../Hooks/useModal';
import useLesson from '../../Hooks/useLesson';
import useUsers from '../../Hooks/useUsers';
import ModalInformation from '../../Components/ModalInformation';
import useDropZone from '../../../../Hooks/useDropZone';
import Title from '../../Components/Title';
import PrivateLessonForm from '../../Components/PrivateLesson';
import SelectContent from './selectContent';
import { nameStyle, backdropStyle } from './style';

function LessonVideo({
  addLesson,
  module,
  updateLesson,
  currentPositionLesson,
  deleteLesson,
  updateLessonData,
  courseId,
  alert,
  setAlert,
}) {
  const [unchanged, setUnchanged] = useState(true);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const lesson = module?.lessons?.[currentPositionLesson];
  const { t2 } = useTranslation('EditCourse');
  const { users, enrolledUsers } = useUsers({ courseId, lessonId: lesson?.id });
  const { openModal, setOpenModal } = useModal();

  useEffect(() => {
    if (alert) {
      setAlertMessage(t2(`${alert}`));
      setAlertOpen(true);
      setTimeout(() => {
        setAlert('');
      }, 2000);
    }
  }, [alert]);

  const {
    name,
    loading,
    isDraft,
    isPrivate,
    privateUsers,
    setPrivateUsers,
    setName,
    setIsDraft,
    setIsPrivate,
    onSave,
    onDelete,
    onUpdate,
    urlVideo,
  } = useLesson({
    addLesson,
    module,
    updateLesson,
    lesson,
    deleteLesson,
    updateLessonData,
  });

  const { acceptedFileItems, fileRejectionItems, acceptedFiles, getRootProps, getInputProps } = useDropZone({
    formats: ['.mp4', '.mov', '.wmv', '.ogx'],
    maxSize: 2147483648,
  });

  const enableButton = () => {
    if (lesson?.id) return t2('SaveChanges');
    if (name?.length === 0 || acceptedFileItems?.length === 0) return null;

    return t2('Save');
  };

  const handleClick = async () => {
    setOpenModal(false);

    if (lesson?.id) {
      await onUpdate({
        id: lesson?.id,
        newName: name,
        isNewDraft: isDraft,
        isNewPrivate: isPrivate,
      });
      return;
    }

    onSave(acceptedFiles[0]);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleUserSelect = array => {
    setUnchanged(false);
    setPrivateUsers([...array]);
  };

  return (
    <div>
      <Title
        name={name}
        textButton={enableButton()}
        eventButton={() => onSave(acceptedFiles[0])}
        check={isDraft}
        setCheck={setIsDraft}
        setUnchanged={setUnchanged}
        checkText={t2('Draft')}
        checkInfo={t2('DraftCondition')}
        checkDisabled={false}
        setOpenModal={setOpenModal}
        buttonDisabled={unchanged}
      >
        <Options Icon={<MoreVert />} disabledEffect>
          <MenuItem onClick={onDelete}>{t2('DeleteLesson')}</MenuItem>
        </Options>
      </Title>
      <TextField
        label={t2('LessonVideoNameLabel')}
        color="MexlogGreen"
        defaultValue={name}
        onChange={e => {
          setName(e.target.value);
          setUnchanged(false);
        }}
        style={nameStyle}
      />
      <SelectContent
        urlVideo={urlVideo}
        getInputProps={getInputProps}
        getRootProps={getRootProps}
        loading={loading}
        uploadFiles={uploadFiles}
        acceptedFileItems={acceptedFileItems}
        fileRejectionItems={fileRejectionItems}
        setUnchanged={setUnchanged}
      />
      <Alert severity="info" style={{ width: '50%', marginTop: '3rem' }}>
        {t2('AlertMessageVideo')}
      </Alert>
      <PrivateLessonForm
        isPrivate={isPrivate}
        setIsPrivate={setIsPrivate}
        users={users}
        privateUsers={privateUsers}
        selectHandler={handleUserSelect}
        enrolledUsers={enrolledUsers}
        setUnchanged={setUnchanged}
      />
      <Backdrop sx={backdropStyle} open={loading}>
        {t2('UploadVideo')}
      </Backdrop>
      <Modal
        open={openModal}
        setOpen={setOpenModal}
        Children={
          <ModalInformation
            accept={handleClick}
            cancel={handleClose}
            title={lesson?.id ? t2('UpdateLesson') : t2('CreateLesson')}
            message={lesson?.id ? t2('MessageUpdateLesson', ['video']) : t2('MessageCreateLesson', ['video'])}
          />
        }
      />
      <AlertMessage message={alertMessage} open={alertOpen} setOpen={setAlertOpen} />
    </div>
  );
}

export default LessonVideo;
